import { ReactSVG } from 'react-svg';
import styles from './results-step.module.scss';
import { LEVEL_COLORS, LEVEL_TEXT_COLORS, RED_FLAGS } from '../tech-due-diligence-quiz.data';
import { Level, Results } from '../tech-due-diligence-quiz.types';

type ResultsStepProps = {
  results: Results;
};

function sectionLevel(avg: number | null): Level | null {
  if (avg === null) return null;
  if (avg >= 3.5) return 'L4';
  if (avg >= 2.5) return 'L3';
  if (avg >= 1.5) return 'L2';
  return 'L1';
}

function hexToRgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function resultBadgeColors(level: Level | null): { bg: string; border: string; text: string } {
  const color = level ? LEVEL_COLORS[level] : '#dfdedf';
  const text = level ? LEVEL_TEXT_COLORS[level] : '#6b6b6b';
  return { bg: hexToRgba(color, 0.2), border: hexToRgba(text, 0.4), text };
}

const FlagIcon = () => <ReactSVG src="/tech-due-diligence/flag.svg" wrapper="span" className={styles.flagIcon} />;

function bannerStyle(categoryId: string): { background: string; borderColor: string; color: string } {
  const category = RED_FLAGS.categories.find((c) => c.id === categoryId);
  return {
    background: category?.flagBgColor ?? 'transparent',
    borderColor: category ? hexToRgba(category.flagIconColor, 0.4) : 'transparent',
    color: category?.flagIconColor ?? 'inherit',
  };
}

export const ResultFlagBanners = ({ results }: ResultsStepProps) => {
  if (results.instantKillCount === 0 && results.majorMitigationCount === 0 && results.watchItemCount === 0) return null;
  return (
    <div className={styles.banners}>
      {results.instantKillCount > 0 && (
        <span className={styles.banner} style={bannerStyle('instantKill')}>
          <FlagIcon />
          <span>{results.instantKillCount}</span>
        </span>
      )}
      {results.majorMitigationCount > 0 && (
        <span className={styles.banner} style={bannerStyle('majorMitigation')}>
          <FlagIcon />
          <span>{results.majorMitigationCount}</span>
        </span>
      )}
      {results.watchItemCount > 0 && (
        <span className={styles.banner} style={bannerStyle('watchItem')}>
          <FlagIcon />
          <span>{results.watchItemCount}</span>
        </span>
      )}
    </div>
  );
};

const ResultsStep = ({ results }: ResultsStepProps) => {
  return (
    <div className={styles.page}>
      <div className={styles.list}>
        {results.sectionScores.map(({ section, avg }) => {
          const { bg, border, text } = resultBadgeColors(sectionLevel(avg));
          return (
            <div className={styles.row} key={section.id}>
              <span className={styles.rowTitle}>{section.title}</span>
              <span className={styles.rowBadge} style={{ background: bg, borderColor: border, color: text }}>
                {avg !== null ? avg.toFixed(2) : '—'} / 4.0
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResultsStep;
